import type { ReactNode } from "react";
import { useId, useState } from "react";
import { useTranslations } from "use-intl";

import { ExpandIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

interface ArtistDirectoryDisclosureProps {
  children: ReactNode;
  collapsible: boolean;
}

export function ArtistDirectoryDisclosure({
  children,
  collapsible,
}: ArtistDirectoryDisclosureProps) {
  const t = useTranslations("artistDirectory");
  const contentId = useId();
  const [expanded, setExpanded] = useState(false);

  if (!collapsible) return children;

  return (
    <div className="relative">
      <div
        id={contentId}
        className={cn(
          "relative",
          expanded
            ? "max-h-none"
            : "max-h-[26rem] overflow-hidden focus-within:max-h-none md:max-h-[22rem]",
        )}
        onFocusCapture={() => setExpanded(true)}
      >
        {children}
        {expanded ? null : (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent via-background/85 to-background"
          />
        )}
      </div>
      <div className={cn("relative z-10 flex justify-center", expanded ? "mt-6" : "-mt-12")}>
        <button
          type="button"
          aria-controls={contentId}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {expanded ? t("showLess") : t("showMore")}
          <ExpandIcon
            decorative
            className={cn("text-xl transition-transform", expanded && "rotate-180")}
          />
        </button>
      </div>
    </div>
  );
}
